/**
 * Operate-UI client for the authoring endpoints (creating new Cognos
 * content into a staging folder, then promoting it). Mirrors saveVizApi:
 * one-shot request/response, structured-error surfacing.
 *
 * Authoring is OFF by default — {@link getAuthoringConfig} drives whether
 * the UI shows the "stage for review" affordance at all.
 */
import { AxiosError } from "axios";
import { api } from "./api";

export interface AuthoringConfig {
  enabled:           boolean;
  allowExplorations: boolean;
  requireStaging:    boolean;
  stagingFolder:     string;
  stagingConfigured: boolean;
}

export interface AuthorResult {
  success:    boolean;
  storeId:    string | null;
  searchPath: string | null;
  folder:     string | null;
}

/**
 * One report idea returned by {@link suggestReports} — a fully-specified
 * plan (layout + roles) the designer derived from the model's schema, so
 * the UI can preview/author it directly without a second round-trip.
 */
export interface ReportIdea {
  title:       string;
  rationale?:  string;
  layoutKind?: string;       // "list" | "crosstab" | "chart"
  vizType?:    string;       // "bar" | "column" | "line" | ""
  categories?: string[];     // dimension identifiers
  measures?:   string[];     // fact identifiers
  prompt?:     string;       // ready-to-run authoring instruction
}

/** Result of a transient (no-persist) native-report run. */
export interface PreviewResult {
  success:    boolean;
  layoutKind: string;
  columns:    string[];
  rows:       Array<Array<unknown>>;
  rowCount:   number;
}

/** Fields shared by the preview + native-report requests. */
export interface NativePlanInput {
  modelId:     string;
  intent?:     string;
  layoutKind?: string;
  vizType?:    string;
  columns?:    string[];
  categories?: string[];
  measures?:   string[];
}

export interface AuthoringApiError {
  code:   string;
  detail: string;
  status: number;
}

const DISABLED: AuthoringConfig = {
  enabled: false, allowExplorations: false,
  requireStaging: true, stagingFolder: "", stagingConfigured: false,
};

/** Capability probe — never throws; a failed/forbidden probe means "off". */
export async function getAuthoringConfig(): Promise<AuthoringConfig> {
  try {
    const { data } = await api.get<Partial<AuthoringConfig>>("/authoring/config");
    return {
      enabled:           data?.enabled === true,
      allowExplorations: data?.allowExplorations === true,
      requireStaging:    data?.requireStaging !== false,
      stagingFolder:     typeof data?.stagingFolder === "string" ? data.stagingFolder : "",
      stagingConfigured: data?.stagingConfigured === true,
    };
  } catch {
    return DISABLED;
  }
}

/** Author a report into the staging folder (governed workflow). */
export async function authorReport(params: {
  reportName:         string;
  subtitle?:          string;
  columns:            string[];
  rows:               Array<Array<unknown>>;
  chartImageDataUrl?: string;
  targetFolder?:      string;
}): Promise<AuthorResult> {
  return post("/authoring/report", {
    reportName: params.reportName,
    subtitle:   params.subtitle && params.subtitle.trim() !== "" ? params.subtitle : undefined,
    columns:    params.columns,
    rows:       params.rows,
    chartImageDataUrl:
      params.chartImageDataUrl && params.chartImageDataUrl.trim() !== "" ? params.chartImageDataUrl : undefined,
    targetFolder: params.targetFolder,
  });
}

/** Promote (move) a staged object to its real destination folder. */
export async function promoteAuthored(
  source: string,
  targetFolder: string,
  newName?: string,
): Promise<AuthorResult> {
  return post("/authoring/promote", {
    source,
    targetFolder,
    newName: newName && newName.trim() !== "" ? newName : undefined,
  });
}

/**
 * Suggest report ideas for a data module / package. Read-only — needs
 * the SDK + LLM gateway, NOT the authoring write-flag. Each idea is a
 * ready-to-preview/author plan.
 */
export async function suggestReports(modelId: string, count?: number): Promise<ReportIdea[]> {
  try {
    const { data } = await api.post<{ suggestions?: unknown }>("/authoring/suggest", {
      modelId,
      count,
    });
    const raw = Array.isArray(data?.suggestions) ? data!.suggestions : [];
    return (raw as Array<Record<string, unknown>>)
      .filter((s) => s && typeof s.title === "string")
      .map((s) => ({
        title:      String(s.title),
        rationale:  typeof s.rationale === "string" ? s.rationale : undefined,
        layoutKind: typeof s.layoutKind === "string" ? s.layoutKind : undefined,
        vizType:    typeof s.vizType === "string" ? s.vizType : undefined,
        categories: toStringArray(s.categories),
        measures:   toStringArray(s.measures),
        prompt:     typeof s.prompt === "string" ? s.prompt : undefined,
      }));
  } catch (e) {
    throw toAuthoringError(e);
  }
}

/**
 * Run a native report transiently and return its rows WITHOUT persisting
 * anything — the "preview before persist" step. Read-only (no write
 * flag); needs only the SDK.
 */
export async function previewNativeReport(plan: NativePlanInput): Promise<PreviewResult> {
  try {
    const { data } = await api.post<Partial<PreviewResult>>("/authoring/preview", nativeBody(plan));
    return {
      success:    data?.success === true,
      layoutKind: typeof data?.layoutKind === "string" ? data.layoutKind : "list",
      columns:    toStringArray(data?.columns),
      rows:       Array.isArray(data?.rows) ? (data!.rows as Array<Array<unknown>>) : [],
      rowCount:   typeof data?.rowCount === "number" ? data.rowCount : 0,
    };
  } catch (e) {
    throw toAuthoringError(e);
  }
}

/**
 * Author a NATIVE live-query report (real query bound to the model) into
 * the staging folder. Gated by datumcraft.authoring.enabled on the
 * backend.
 */
export async function authorNativeReport(params: NativePlanInput & {
  reportName:   string;
  targetFolder?: string;
}): Promise<AuthorResult> {
  return post("/authoring/native-report", {
    ...nativeBody(params),
    reportName:   params.reportName,
    targetFolder: params.targetFolder,
  });
}

/** Build the shared {modelId, intent?, layoutKind?, …} request body, omitting empties. */
function nativeBody(plan: NativePlanInput): Record<string, unknown> {
  return {
    modelId:    plan.modelId,
    intent:     plan.intent && plan.intent.trim() !== "" ? plan.intent : undefined,
    layoutKind: plan.layoutKind && plan.layoutKind.trim() !== "" ? plan.layoutKind : undefined,
    vizType:    plan.vizType && plan.vizType.trim() !== "" ? plan.vizType : undefined,
    columns:    notEmpty(plan.columns) ? plan.columns : undefined,
    categories: notEmpty(plan.categories) ? plan.categories : undefined,
    measures:   notEmpty(plan.measures) ? plan.measures : undefined,
  };
}

function notEmpty(xs?: string[]): boolean {
  return Array.isArray(xs) && xs.length > 0;
}

function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string" && x.trim() !== "");
}

async function post(path: string, body: unknown): Promise<AuthorResult> {
  try {
    const { data } = await api.post<Partial<AuthorResult>>(path, body);
    return {
      success:    data?.success === true,
      storeId:    typeof data?.storeId === "string" ? data.storeId : null,
      searchPath: typeof data?.searchPath === "string" ? data.searchPath : null,
      folder:     typeof data?.folder === "string" ? data.folder : null,
    };
  } catch (e) {
    throw toAuthoringError(e);
  }
}

function toAuthoringError(e: unknown): AuthoringApiError | unknown {
  if (e instanceof AxiosError) {
    const b = e.response?.data as { error?: string; detail?: string } | undefined;
    return {
      code:   typeof b?.error  === "string" ? b.error  : "http.status",
      detail: typeof b?.detail === "string" ? b.detail : (e.message ?? `HTTP ${e.response?.status}`),
      status: e.response?.status ?? 0,
    } satisfies AuthoringApiError;
  }
  return e;
}
